import { FC, RefObject, useCallback } from 'react';

import ItemPageActions from '~/app/components/ItemPage/components/ItemPageActions';
import useUnpublishLivePage from '~/app/components/ItemPage/hooks/useUnpublishLivePage';
import deleteAlbumAction from '~/app/lib/store/albums/actions/deleteAlbum';
import useFetchSessionUser from '~/app/lib/hooks/useFetchSessionUser';
import useSelectArtists from '~/app/lib/hooks/useSelectArtists';
import { toPublicEndpoint } from '~/app/lib/getPublicEndpoint';
import { toShareUrl } from '~/app/components/ItemPage/utils';
import { useDispatch } from '~/app/lib/store/redux';
import { useAppRouter } from '~/app/lib/router2';
import useHash from '~/app/lib/hooks/useHash';
import { useI18n } from '~/app/lib/i18n';
import { ItemTypes } from '~/types';

import {
  useAppConfirm,
  useAppToast,
} from '~/app/components/NextApp/lib/CoreUi';

import { AlbumItemContext } from '../types';

export const AlbumPageActions: FC<{
  itemContext: AlbumItemContext;
  isOpen: boolean;
  actionButtonRef: RefObject<HTMLElement>;
  onClose: () => void;
}> = ({ itemContext, isOpen, actionButtonRef, onClose }) => {
  const { isLoggedIn, user } = useFetchSessionUser();
  const showConfirmDialog = useAppConfirm();
  const { showArtistIds, hasArtistActions } = itemContext.config;
  const artists = useSelectArtists(showArtistIds);
  const artistNames = artists?.map(({ name }) => name).join(', ');
  const { setHashParam } = useHash();
  const unpublishPage = useUnpublishLivePage(ItemTypes.ALBUM);
  const router = useAppRouter();
  const album = itemContext.data.item;
  const dispatch = useDispatch();
  const showToast = useAppToast();
  const { t } = useI18n();

  const shareUrl = toShareUrl({
    isOwned: album.isOwned,
    isDraft: album.isDraft,
    pageBrand: album.pageBrand,
  });

  const shareText = album.name && `"${album.name}" by ${artistNames}`;

  return (
    <ItemPageActions
      isOpen={isOpen}
      item={album}
      targetElRef={actionButtonRef}
      onClose={onClose}
      userCanEdit={!!album.userCanEdit}
      pagePath={album.pagePath}
      shareUrl={shareUrl}
      shareText={shareText}
      displayType="album"
      toActionItems={useCallback(
        ({ sections }) => {
          if (album.userCanEdit) {
            if (album.isPrerelease) {
              // Prerelease pages have an "Edit details" action that shows a dialog where
              // (non-orchard) owners can change the name, upc and releaseDate. If the page
              // is in 'live' state it can also be unpublished to revert it back to draft.
              sections[0].push({
                content: t('item.actions.editAlbumDetails'),
                testId: 'editDetails',
                icon: 'info',

                onClick: () => {
                  setHashParam({ editDetails: '' });
                },
              });

              // don't allow upgrade and unpublish of draft pages
              if (!album.isDraft) {
                sections[0].push({
                  content: t('item.actions.upgradePrerelease'),
                  href: user?.featureFlags['songwhip_prerelease_isrc']
                    ? `${album.pagePath}/upgrade`
                    : `${album.pagePath}/release`,
                  testId: 'upgradePrerelease',
                  icon: 'publish',
                });

                sections[sections.length - 1].push({
                  testId: 'unpublishPage',
                  content: t('item.actions.unpublishPage'),
                  icon: 'unpublish',
                  onClick: () => {
                    unpublishPage({
                      itemId: album.id,
                      itemName: album.name,
                      pagePath: album.pagePath,
                    });
                  },
                });
              }
            }

            sections[0].push({
              content: t('item.actions.viewInDashboard'),
              href: `/dashboard/album/${album.id}`,
              icon: 'chart',
              testId: 'viewInDashboard',
            });

            sections[sections.length - 1].push({
              content: t('item.actions.deletePage'),
              testId: 'deletePage',
              icon: 'delete',

              onClick: () => {
                showConfirmDialog({
                  content: t('item.albumPageDeleteText'),
                  actionText: t('app.actions.delete'),
                  cancelText: t('app.actions.cancel'),

                  onConfirm: async () => {
                    await dispatch(
                      deleteAlbumAction({
                        albumId: album.id,
                      })
                    );

                    showToast({
                      text: t('item.events.albumPageDeleted'),
                    });

                    router.push('/create');
                  },
                });
              },
            });
          } else if (isLoggedIn) {
            // If the user doesn't own the album,
            // they can try to claim it.
            sections[0].push({
              content: t('item.actions.claimAlbum'),
              icon: 'verified',
              testId: 'claimAlbumOption',
              onClick: () => {
                setHashParam({ claimPage: '' });
              },
            });
          }

          if (hasArtistActions && artists) {
            sections.push(
              artists.map(({ name, pagePath }) => ({
                content: name,
                href: toPublicEndpoint(pagePath),
                icon: 'person',
              }))
            );
          }

          // if (toActionItems) {
          //   return toActionItems({ sections });
          // }

          return sections;
        },
        [album]
      )}
    />
  );
};
